import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Users, Eye, TrendingUp, Globe, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getConsentStatus } from '@/lib/analytics';

interface PageViewEntry {
  path: string;
  timestamp: number;
}

// Simple in-memory tracker for real-time-like stats (client-side only)
// GA4 doesn't expose a client-side real-time API, so we track locally
const SESSION_KEY = 'opsgpt-pageviews-session';

function getSessionPageViews(): PageViewEntry[] {
  try {
    const data = sessionStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function recordPageView(path: string) {
  const views = getSessionPageViews();
  views.push({ path, timestamp: Date.now() });
  // Keep last 100 entries
  const trimmed = views.slice(-100);
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(trimmed));
}

export function RealtimeAnalyticsWidget() {
  const [stats, setStats] = useState({
    currentPage: '/',
    totalViews: 0,
    uniquePages: 0,
    sessionDuration: '0m',
    recentPages: [] as { path: string; count: number }[],
    analyticsEnabled: false,
  });

  useEffect(() => {
    const update = () => {
      const views = getSessionPageViews();
      const now = Date.now();
      const analyticsEnabled = getConsentStatus() === 'accepted';

      // Session duration
      const firstView = views[0]?.timestamp || now;
      const durationMs = now - firstView;
      const durationMin = Math.floor(durationMs / 60000);
      const durationSec = Math.floor((durationMs % 60000) / 1000);
      const sessionDuration = durationMin > 0 ? `${durationMin}m ${durationSec}s` : `${durationSec}s`;

      // Count by page
      const pageCounts = new Map<string, number>();
      views.forEach(v => {
        pageCounts.set(v.path, (pageCounts.get(v.path) || 0) + 1);
      });

      const recentPages = Array.from(pageCounts.entries())
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const currentPage = views.length > 0 ? views[views.length - 1].path : '/';

      setStats({
        currentPage,
        totalViews: views.length,
        uniquePages: pageCounts.size,
        sessionDuration,
        recentPages,
        analyticsEnabled,
      });
    };

    update();
    const interval = setInterval(update, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card variant="glass">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Live Session Analytics
          <span className="relative flex h-2.5 w-2.5 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard 
            icon={<Eye className="w-4 h-4" />}
            label="Page Views" 
            value={stats.totalViews.toString()} 
          />
          <StatCard 
            icon={<Globe className="w-4 h-4" />}
            label="Unique Pages" 
            value={stats.uniquePages.toString()} 
          />
          <StatCard 
            icon={<Clock className="w-4 h-4" />}
            label="Session Time" 
            value={stats.sessionDuration} 
          />
          <StatCard 
            icon={<TrendingUp className="w-4 h-4" />}
            label="GA Status" 
            value={stats.analyticsEnabled ? 'Active' : 'Off'}
            valueColor={stats.analyticsEnabled ? 'text-emerald-400' : 'text-muted-foreground'}
          />
        </div>

        {/* Current Page */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Current page:</span>
          <Badge variant="secondary" className="font-mono text-xs">
            {stats.currentPage}
          </Badge>
        </div>

        {/* Top Pages */}
        {stats.recentPages.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Top Pages This Session
            </p>
            <div className="space-y-1.5">
              {stats.recentPages.map((page, i) => (
                <motion.div
                  key={page.path}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-mono text-xs text-foreground/80 truncate max-w-[200px]">
                    {page.path}
                  </span>
                  <div className="flex items-center gap-2">
                    <div 
                      className="h-1.5 rounded-full bg-primary/60"
                      style={{ 
                        width: `${Math.max(20, (page.count / stats.totalViews) * 100)}px` 
                      }}
                    />
                    <span className="text-xs text-muted-foreground w-6 text-right">
                      {page.count}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground pt-1">
          Session-level tracking • Full analytics available in{' '}
          <a 
            href="https://analytics.google.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:text-foreground transition-colors"
          >
            Google Analytics
          </a>
        </p>
      </CardContent>
    </Card>
  );
}

function StatCard({ 
  icon, label, value, valueColor = 'text-foreground' 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-1">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <p className={`text-lg font-bold ${valueColor}`}>{value}</p>
    </div>
  );
}
